import { AutocompleteState, BaseItem } from "@algolia/autocomplete-core";
import { isDefined } from "@omni/utils/helper.utils";
import classnames from "classnames";
import { isEmpty } from "lodash-es";
import { MouseEvent, RefObject } from "react";

import { ResultsPanelComponent } from "@/components/Search/SearchBar/ResultsPanel/resultsPanel.component";
import { getResultsPanelBlocks } from "@/components/Search/SearchBar/ResultsPanel/resultsPanel.templates";
import { AutocompleteRef } from "@/components/Search/SearchBar/searchBar.types";
import { AutocompleteActions } from "@/components/Search/SearchBar/useAutocompleteApi.hook";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { KeyNames } from "@/utils/enums";
import { createKeyHandler } from "@/utils/keyboard";

import { getLastAutocompleteItem } from "../helpers/accessibility.helpers";
import "./resultsPanel.scss";

type ResultsPanelProps = {
    onMouseDown: (event: MouseEvent) => void;
    items?: Record<string, BaseItem[]>;
    autocompleteState?: AutocompleteState<BaseItem>;
    autocompleteActions?: AutocompleteActions;
    autocompleteRef: RefObject<AutocompleteRef>;
};

const ResultsPanel = ({
    onMouseDown,
    items,
    autocompleteState,
    autocompleteActions,
    autocompleteRef
}: ResultsPanelProps) => {
    const { t } = useGranite();

    if (
        !isDefined(items) ||
        !isDefined(autocompleteState) ||
        !isDefined(autocompleteActions)
    ) {
        return null;
    }

    const blocks = getResultsPanelBlocks(
        items,
        autocompleteState,
        autocompleteActions
    );

    const hasResults =
        !!Object.entries(items).length &&
        Object.values(items).some((resultList) => resultList.length > 0);

    const noResults =
        isEmpty(autocompleteState?.query) || hasResults
            ? null
            : t("searchBox.panel.noResults");

    const handleResultsPanelKeyDown = createKeyHandler([
        {
            keys: [KeyNames.TAB],
            handler: (event) => {
                if (event.shiftKey) return;

                const lastAutocompleteItem = getLastAutocompleteItem();

                if (
                    isDefined(lastAutocompleteItem) &&
                    event.target === lastAutocompleteItem
                ) {
                    event.preventDefault();
                    autocompleteRef.current.input?.focus();
                }
            }
        },
        {
            keys: [KeyNames.ENTER],
            handler: () => {
                const focussedElement = document.activeElement;
                if (!(focussedElement instanceof HTMLAnchorElement)) return;

                focussedElement.click();
            }
        }
    ]);

    return (
        <div
            className={classnames("resultsPanel")}
            onMouseDown={onMouseDown}
            onKeyDown={handleResultsPanelKeyDown}
        >
            <ResultsPanelComponent
                blocks={blocks}
                noResults={noResults}
            />
        </div>
    );
};

export { ResultsPanel };
